import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { UploadSimple, CheckCircle, WarningCircle, FileXls } from "@phosphor-icons/react";
import React from "react";

function ExcelImport() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
      setFile(f); setResult(null);
    } else {
      toast.error("Please select an Excel file (.xlsx or .xls)");
    }
  };

  const handleImport = async () => {
    if (!file) { toast.error("Select a file first"); return; }
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/excel/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      toast.success(`Imported ${res.data.imported_count} records`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{fontFamily: 'Chivo'}}>Excel Import</h1>
          <p className="text-sm text-[#64748B] mt-1">Smart import - handles case/spacing variations automatically</p>
        </div>

        <Card className="p-6 border border-[#E2E8F0] space-y-5">
          <div>
            <h3 className="text-lg font-bold text-[#1D2951] mb-2">Upload Excel File</h3>
            <p className="text-sm text-[#64748B]">
              Import handles flexible headers: "Part No", "part_no", "PART NO" all work.
              Scheduled Quantity is summed (not replaced). Opening Balance is stored per operation.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-[#1D2951] mb-1">Supported Columns:</h4>
              <ul className="space-y-0.5 text-[#64748B]">
                <li>Part No (required - must exist in Item Master)</li>
                <li>Scheduled Quantity (adds to existing)</li>
                <li>Opening Balance</li>
                <li>Issued Quantity, Received Quantity, OK Quantity</li>
                <li>Rejected, Rework, Produced</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#1D2951] mb-1">Smart Features:</h4>
              <ul className="space-y-0.5 text-[#64748B]">
                <li>Case insensitive headers</li>
                <li>Ignores extra spaces</li>
                <li>Skips invalid rows (no fail)</li>
                <li>Converts text to numbers</li>
                <li>Validates Part No exists</li>
              </ul>
            </div>
          </div>

          <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-6 text-center hover:border-[#F5DF4D] transition-colors">
            <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" id="file-upload" data-testid="file-input" />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 bg-[#F5DF4D] rounded-full flex items-center justify-center">
                  <UploadSimple size={28} weight="bold" className="text-[#000]" />
                </div>
                <p className="text-base font-semibold text-[#1D2951]">Choose Excel File</p>
                {file && <p className="text-sm font-mono text-[#64748B] bg-[#F8FAFC] px-3 py-1 rounded">{file.name}</p>}
              </div>
            </label>
          </div>

          <Button onClick={handleImport} disabled={!file || importing}
            className="w-full bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px] font-semibold"
            data-testid="import-button">
            {importing ? "Importing..." : "Import Data"}
          </Button>

          {result && (
            <div className="space-y-3" data-testid="import-result">
              <Alert className="border-l-4 border-l-[#10B981] bg-green-50">
                <CheckCircle size={18} weight="fill" className="text-[#10B981]" />
                <AlertDescription>
                  <span className="font-semibold text-[#10B981]">Imported {result.imported_count} records</span>
                </AlertDescription>
              </Alert>
              {result.errors?.length > 0 && (
                <Alert className="border-l-4 border-l-[#F59E0B] bg-yellow-50">
                  <WarningCircle size={18} weight="fill" className="text-[#F59E0B]" />
                  <AlertDescription>
                    <p className="font-semibold text-[#F59E0B] mb-1">{result.errors.length} errors:</p>
                    <ul className="list-disc list-inside text-xs text-[#64748B] max-h-32 overflow-y-auto">
                      {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

export default ExcelImport;
